import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import FocusTrap from 'focus-trap-react';
import { IconCrossLarge } from '../../../../foundation/icons/IconCrossLarge/index.js';
import { useStyles } from '../styles/index.js';
import { Overlay } from '../../../atoms/Overlay/Overlay/index.js';
import { ButtonSkinTertiary } from '../../../atoms/Button/skins/index.js';
import { Button } from '../../../atoms/Button/Button/index.js';
import { ButtonSizeSM } from '../../../atoms/Button/sizes/index.js';
import { isDesktopDevice } from '../../../../foundation/utils/getDeviceType/getDeviceType/index.js';

const Popup = forwardRef(({ children, onClose, className, isShown = false, zIndex, contentClassName, coverage, hasOutSideClick = true, dataPulseName, contentIsFullWidth, dataPulseComponent, dataPulseComponentType, ...props }, ref) => {
    const popupContentRef = useRef(null);
    const isMobile = !isDesktopDevice;
    const styles = useStyles({ isMobile });
    const [isOpened, setIsOpened] = useState(isShown);
    const [isMouseDownInside, setMouseDownInside] = useState(false);
    const isControlled = onClose !== undefined;
    useEffect(() => {
        if (isControlled) {
            setIsOpened(isShown);
        }
    }, [isControlled, isShown]);
    const handleMouseDownInside = () => {
        setMouseDownInside(true);
    };
    useEffect(() => {
        const handleMouseUp = () => {
            setTimeout(() => setMouseDownInside(false), 0);
        };
        const handleClickOutside = (event) => {
            if (!hasOutSideClick)
                return;
            if (popupContentRef?.current && !popupContentRef.current.contains(event.target) && !isMouseDownInside) {
                if (isControlled) {
                    onClose?.();
                }
                else {
                    setIsOpened(false);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [hasOutSideClick, isMouseDownInside, onClose, isControlled]);
    const handleClose = useCallback((isCloseIcon) => {
        if (isControlled) {
            if (isCloseIcon) {
                onClose?.();
            }
            else {
                !isMouseDownInside && onClose();
            }
        }
        else {
            setIsOpened(false);
        }
    }, [isControlled, isMouseDownInside, onClose]);
    useEffect(() => {
        if (!isControlled && isShown) {
            setIsOpened(true);
        }
    }, [isControlled, isShown]);
    return isOpened ? (jsx(Overlay, { ref: ref, className: className, "data-testid": props['data-testid'], zIndex: zIndex, closeCallback: hasOutSideClick ? handleClose : undefined, coverage: coverage, contentIsFullWidth: contentIsFullWidth, children: jsx(FocusTrap, { focusTrapOptions: {
                clickOutsideDeactivates: hasOutSideClick,
                allowOutsideClick: () => !hasOutSideClick,
                initialFocus: false,
                preventScroll: true,
            }, children: jsx("div", { className: clsx(styles.root, contentClassName), "data-testid": "container", ref: popupContentRef, "data-pulse-component": dataPulseComponent, "data-pulse-component-type": dataPulseComponentType, onMouseDown: handleMouseDownInside, children: jsxs("div", { className: styles.holder, children: [jsx(Button, { className: styles.closeButton, skin: ButtonSkinTertiary, dataTestId: "button", "data-pulse-name": dataPulseName, centerIcon: IconCrossLarge, size: ButtonSizeSM, onClick: () => handleClose(true) }), jsx("div", { className: styles.content, "data-testid": "content-popup", children: children })] }) }) }) })) : null;
});
Popup.displayName = 'Popup';

export { Popup };
